import { POOL_ADDRESS } from "../constants/addresses";
import { Contract } from "@ethersproject/contracts";
import PoolABI from "../constants/abis/staking-pool.json";
import LPPoolABI from "../constants/abis/lp-staking-pool.json";

import ERC20Interface from "../constants/abis/ERC20.json";

import { useMemo } from "react";
import { ethers } from "ethers";
import { useActiveWeb3React } from "./useActiveWeb3React";
import { getContract } from "../functions/contract";
import Web3 from "web3";
import { NETWORK_BSC, NETWORK_FUJI } from "../constants/index";
import RPC from "../config/rpc";
import { useGetMainNetwork } from "../state/application/hooks";
import { MULTICALL_ABI, MULTICALL_NETWORKS } from "../constants/multicall";
import { chain } from "lodash";
const DEFAULT_CHAINID = Number(process.env.NEXT_PUBLIC_CHAINID);

const defaultProvider = {};

export function useContract(
  address: string | undefined,
  ABI: any,
  withSignerIfPossible = true
): Contract | null {
  const { library, account, chainId } = useActiveWeb3React();

  return useMemo(() => {
    const finalLibrary = defaultProvider[chainId];

    if (!address || !ABI || !account) return null;
    if (!library) {
      const contract = new ethers.Contract(address, ABI, finalLibrary);
      if (contract) return contract;
      return null;
    }

    try {
      return getContract(
        address,
        ABI,
        library,
        withSignerIfPossible && account ? account : undefined
      );
    } catch (error) {
      return null;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, ABI, library, withSignerIfPossible, chainId, account]);
}

export function useTokenBalance(tokenAddress: string) {
  return useContract(tokenAddress, ERC20Interface);
}

export function useMulticallContract(): Contract | null {
  const { chainId } = useActiveWeb3React();
  return useContract(
    chainId && MULTICALL_NETWORKS[chainId],
    MULTICALL_ABI,
    false
  );
}

export function useEtherBalance() {
  const { account, chainId } = useActiveWeb3React();
  return useContract(MULTICALL_NETWORKS[chainId], MULTICALL_ABI);
}

export function useAllowance(tokenAddress: string): undefined | any {
  return useContract(tokenAddress, ERC20Interface);
}

export function useTokenContract(tokenAddress?: string) {
  return useContract(tokenAddress, ERC20Interface);
}

export const useContractWithoutConnectWallet = (address: any, abi: any) => {

  const chainId = useGetMainNetwork();
  const { account } = useActiveWeb3React();
  const CHAINISUSE = account && DEFAULT_CHAINID === chainId ? chainId : DEFAULT_CHAINID
  const web3 = new Web3(RPC[CHAINISUSE]);

  const address_pool = address && address[CHAINISUSE] ? address[CHAINISUSE] : address[CHAINISUSE]
  const contract = new web3.eth.Contract(abi as any, address_pool);
  return contract;
};

export function usePoolInfoWithoutConnectWallet() {
  const contract = useContractWithoutConnectWallet(
    POOL_ADDRESS,
    PoolABI
  )?.methods;
  return contract;
}

export function usePoolInfo() {
  const { account } = useActiveWeb3React();
  const chainId = useGetMainNetwork();
  const pool = usePoolContract();
  const poolWithout = usePoolInfoWithoutConnectWallet();
  return account && DEFAULT_CHAINID === chainId ? pool : poolWithout;
}

export function usePoolContract() {
  // const { chainId } = useActiveWeb3React();

  const chainId = Number(process.env.NEXT_PUBLIC_CHAINID);
  return useContract(POOL_ADDRESS[chainId], PoolABI);
}

export function useLPPoolInfoWithoutConnectWallet(address: any) {
  const contract = useContractWithoutConnectWallet(address, LPPoolABI)?.methods;
  return contract;
}

export function useLPPoolContract(address: any) {
  // const { chainId } = useActiveWeb3React();
  const chainId = Number(process.env.NEXT_PUBLIC_CHAINID);

  return useContract(address[chainId], LPPoolABI);
}

export function useLPPoolInfo(address: any) {
  const { account, chainId } = useActiveWeb3React();

  const pool = useLPPoolContract(address);
  const poolWithout = useLPPoolInfoWithoutConnectWallet(address);


  return account && DEFAULT_CHAINID === chainId ? pool : poolWithout;
}
